/**
 * Floating assistant launcher.
 *
 * Mounted once by `PublicLayout`, so it rides every public page. Deliberately
 * NOT a chat popover: the assistant needs room for citations and a transcript,
 * and a cramped bubble would push the sources out of view — which is the one
 * part of a RAG answer the reader must be able to check. This is a link to the
 * full page, not a mini surface.
 *
 * Hidden on `/assistant` itself: a button that navigates to the page you are
 * already on is noise, and it would sit on top of the composer.
 */

import { Link, useLocation } from 'react-router-dom'
import { Bot } from 'lucide-react'
import { cn } from '@/shared/lib/cn'
import { defineT, useTranslation } from '@/shared/i18n/use-translation'

const T = defineT({
  label: { ar: 'اسأل المساعد', en: 'Ask the assistant' },
})

export function AssistantLauncher() {
  const { t } = useTranslation(T)
  const { pathname } = useLocation()

  // Already there — nothing to launch.
  if (pathname.startsWith('/assistant')) return null

  return (
    <Link
      to="/assistant"
      aria-label={t('label')}
      title={t('label')}
      className={cn(
        // Physical `right-5` so the launcher is always bottom-right,
        // regardless of dir. The previous `end-5` placed it on the left
        // in RTL (Arabic), which the UX review flagged as confusing.
        'group fixed bottom-5 right-5 z-40 inline-flex items-center gap-2',
        'h-12 rounded-full border border-primary/30 bg-primary px-4',
        'text-sm font-semibold text-primary-foreground',
        'shadow-glow-sm transition-all duration-300',
        'hover:bg-primary/90 hover:shadow-glow',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        // Collapse to a circle on small screens so it never covers content.
        'w-12 justify-center px-0 sm:w-auto sm:px-4',
      )}
    >
      <Bot className="h-5 w-5 shrink-0" aria-hidden />
      {/* Label is decorative on mobile; the aria-label carries the meaning. */}
      <span className="hidden sm:inline">{t('label')}</span>
    </Link>
  )
}
